import { useCallback, useEffect, useState } from "react";

import { humanizeApiError } from "../api/client";
import {
  type ExtensionHealthIssue,
  type ExtensionHealthListResponse,
  type MailClient,
  extensionHealthApi,
} from "../api/extensionHealth";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Pagination } from "../components/ui/pagination";
import { TableSkeleton } from "../components/ui/table-skeleton";

const PAGE_SIZE = 20;
const REFRESH_MS = 60_000;
const RESOLVE_TOAST_MS = 4_000;

const MAIL_CLIENT_LABEL: Record<MailClient, string> = {
  gmail: "Gmail",
  outlook_web: "Outlook Web",
  yandex: "Яндекс.Почта",
};

const MAIL_CLIENT_FILTERS: { label: string; value: MailClient | "" }[] = [
  { label: "Все клиенты", value: "" },
  { label: "Gmail", value: "gmail" },
  { label: "Outlook Web", value: "outlook_web" },
  { label: "Яндекс.Почта", value: "yandex" },
];

type ResolvedFilter = "open" | "resolved" | "all";

const RESOLVED_FILTERS: { label: string; value: ResolvedFilter }[] = [
  { label: "Требуют проверки", value: "open" },
  { label: "Разобранные", value: "resolved" },
  { label: "Все", value: "all" },
];

// Известные ключи селекторов из extension/src/shared/config.ts — даём
// менеджеру по продажам человеческое название того, что сломалось,
// вместо CSS-строки.
const SELECTOR_LABEL: Record<string, string> = {
  compose_root: "Окно нового письма",
  to_field: "Поле «Кому»",
  subject_field: "Поле «Тема»",
  body: "Тело письма",
  send_button: "Кнопка «Отправить»",
  attachment_chip: "Прикреплённый файл",
  attachment_remove: "Кнопка удалить вложение",
};

function selectorLabel(key: string): string {
  return SELECTOR_LABEL[key] ?? key;
}

function formatDate(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleString("ru-RU", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин назад`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} ч назад`;
  const days = Math.round(hr / 24);
  if (days < 7) return `${days} дн назад`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `${weeks} нед назад`;
  const months = Math.round(days / 30);
  return `${months} мес назад`;
}

function extractMessage(err: unknown): string {
  return humanizeApiError(err);
}

export function ExtensionHealthPage() {
  const [data, setData] = useState<ExtensionHealthListResponse | null>(null);
  const [mailClient, setMailClient] = useState<MailClient | "">("");
  const [resolvedFilter, setResolvedFilter] = useState<ResolvedFilter>("open");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolveToast, setResolveToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resolved =
        resolvedFilter === "open"
          ? false
          : resolvedFilter === "resolved"
            ? true
            : undefined;
      const resp = await extensionHealthApi.list({
        page,
        size: PAGE_SIZE,
        mail_client: mailClient || undefined,
        resolved,
      });
      setData(resp);
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, mailClient, resolvedFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // Автообновление списка: телеметрия пишется фоном, админ хочет
    // видеть свежие записи без F5.
    const t = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (resolveToast === null) return;
    const id = window.setTimeout(() => setResolveToast(null), RESOLVE_TOAST_MS);
    return () => window.clearTimeout(id);
  }, [resolveToast]);

  async function onResolve(issue: ExtensionHealthIssue) {
    setResolvingId(issue.id);
    setError(null);
    try {
      await extensionHealthApi.resolve(issue.id);
      setResolveToast("Запись помечена как разобранная");
      await load();
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setResolvingId(null);
    }
  }

  const items = data?.items ?? [];
  const total = data?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Сбои расширения в почте</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
          Расширение Tracelink встраивает кнопку «Отправить КП» в Gmail,
          Outlook Web и Яндекс.Почту. Когда почтовый сервис меняет вёрстку,
          расширение перестаёт находить нужные элементы — например, кнопку
          «Отправить» или поле «Кому». Каждый такой случай попадает сюда.
        </p>
        <p className="mt-1 text-sm text-muted-foreground max-w-3xl">
          Если в списке есть записи «Требуют проверки» — сообщите разработчику.
          Чинить из админки нельзя: правки делаются в коде расширения. После
          того как сбой починили, отметьте старые записи как разобранные —
          так в списке останутся только актуальные проблемы.
        </p>
      </div>

      {error !== null && (
        <Card>
          <CardContent
            className="text-red-700 text-sm py-3"
            data-testid="extension-health-error"
          >
            {error}
          </CardContent>
        </Card>
      )}

      {resolveToast !== null && (
        <div
          role="status"
          className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
          data-testid="extension-health-resolve-toast"
        >
          {resolveToast}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Фильтры</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 py-3">
          <div>
            <div className="mb-1 text-xs text-muted-foreground">Статус</div>
            <div className="flex flex-wrap items-center gap-2">
              {RESOLVED_FILTERS.map((f) => (
                <Button
                  key={f.value}
                  size="sm"
                  variant={resolvedFilter === f.value ? "default" : "outline"}
                  onClick={() => {
                    setResolvedFilter(f.value);
                    setPage(1);
                  }}
                  data-testid={`resolved-filter-${f.value}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs text-muted-foreground">Почтовый клиент</div>
            <div className="flex flex-wrap items-center gap-2">
              {MAIL_CLIENT_FILTERS.map((f) => (
                <Button
                  key={f.value || "all"}
                  size="sm"
                  variant={mailClient === f.value ? "default" : "outline"}
                  onClick={() => {
                    setMailClient(f.value);
                    setPage(1);
                  }}
                  data-testid={`mail-client-filter-${f.value || "all"}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">
            {resolvedFilter === "open"
              ? "Требуют проверки"
              : resolvedFilter === "resolved"
                ? "Разобранные"
                : "Все записи"}
            <span
              className="ml-2 text-sm font-normal text-muted-foreground"
              data-testid="extension-health-total"
            >
              найдено: {total}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-muted-foreground">
              <tr className="border-b">
                <th className="text-left font-normal px-4 py-2">Почтовый клиент</th>
                <th className="text-left font-normal px-4 py-2">
                  Версия расширения
                </th>
                <th className="text-left font-normal px-4 py-2">Что не работает</th>
                <th className="text-left font-normal px-4 py-2">Когда заметили</th>
                <th className="text-left font-normal px-4 py-2">Статус</th>
                <th className="text-left font-normal px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <TableSkeleton columns={6} />
              ) : items.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-muted-foreground"
                  >
                    {resolvedFilter === "open"
                      ? "Сейчас сбоев нет — расширение работает штатно."
                      : "Записей по этим фильтрам нет."}
                  </td>
                </tr>
              ) : (
                items.map((issue) => (
                  <tr
                    key={issue.id}
                    className="border-b last:border-b-0 align-top"
                    data-testid={`issue-row-${issue.id}`}
                  >
                    <td className="px-4 py-2">
                      {MAIL_CLIENT_LABEL[issue.mail_client]}
                    </td>
                    <td
                      className="px-4 py-2 font-mono text-xs text-muted-foreground"
                      title="Версия расширения у пользователя, у которого произошёл сбой"
                    >
                      {issue.extension_version}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {issue.failed_selectors.map((s) => (
                          <span
                            key={s}
                            className="inline-block rounded bg-red-50 text-red-700 px-2 py-0.5 text-xs"
                            title={`Технический ключ: ${s}`}
                          >
                            {selectorLabel(s)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                      <div>{formatRelative(issue.created_at)}</div>
                      <div className="text-xs">{formatDate(issue.created_at)}</div>
                    </td>
                    <td className="px-4 py-2">
                      {issue.resolved_at !== null ? (
                        <span
                          className="inline-block rounded bg-emerald-50 text-emerald-700 px-2 py-0.5 text-xs"
                          title={`Помечено разобранным ${formatDate(issue.resolved_at)}`}
                        >
                          Разобрано · {formatDate(issue.resolved_at)}
                        </span>
                      ) : (
                        <span
                          className="inline-block rounded bg-amber-50 text-amber-700 px-2 py-0.5 text-xs"
                          title="Сбой ещё не разбирали"
                        >
                          Требует проверки
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {issue.resolved_at === null && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={resolvingId === issue.id}
                          onClick={() => void onResolve(issue)}
                          data-testid={`resolve-${issue.id}`}
                          title="Не чинит проблему — только убирает запись из списка «Требуют проверки». Используйте после того, как разработчик выпустил фикс."
                        >
                          Отметить разобранной
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        disabled={loading}
        onPageChange={(next) => setPage(next)}
      />
    </div>
  );
}
